import React, { ChangeEventHandler, FunctionComponent, useCallback, useMemo } from "react";
import { Box, Grid } from "@mui/material";
import { noop } from "lodash";
import { useTheme } from "@mui/styles";
import { Chip, RoundedIcon } from "@shared/components";
import SelfServiceButton from "@myProfile/components/SelfServiceButton";
import { requestContactUpdateSelector } from "@requestContactUpdate/store/selectors";
import { updateCommonRequestData } from "@requestContactUpdate/store/ducks";
import FileUtils from "@shared/utils/FileUtils";
import useAttachmentValidator from "@requestContactUpdate/hooks/useAttacmentValidator";
import { MimeType } from "@shared/models/mimeType";
import { useAppDispatch, useAppSelector, useMobileLayout } from "@app/hooks";
import { impersonationStateSelector } from "@impersonateUser/store/selectors";
import { ReactComponent as Clip } from "@assets/svg/clip.svg";
import { ReactComponent as ExcelIcon } from "@assets/svg/file_excel.svg";
import { ReactComponent as ImageIcon } from "@assets/svg/file_image.svg";
import { ReactComponent as PdfIcon } from "@assets/svg/file_pdf.svg";
import { ReactComponent as WordIcon } from "@assets/svg/file_word.svg";
import { ReactComponent as DocumentIcon } from "@assets/svg/file_document.svg";
import { ReactComponent as ExclamationIcon } from "@assets/svg/exclamation.svg";

interface Props {
  loading: boolean;
  onSubmit(): void;
}

const RequestContactUpdateControls: FunctionComponent<Props> = ({ onSubmit, loading }) => {
  const {
    validationWarningShown,
    commonFields: { attachment, contactName, contactEmail, message },
  } = useAppSelector(requestContactUpdateSelector);
  const { isImpersonating } = useAppSelector(impersonationStateSelector);

  const dispatch = useAppDispatch();

  const { validateAttachment, allowedExtensions } = useAttachmentValidator();

  const accept = useMemo(
    () => `image/png, ${allowedExtensions.map((ext) => `.${ext}`).join(", ")}`,
    [allowedExtensions]
  );

  const AttachmentIcon = useMemo(() => {
    switch (attachment?.type) {
      case MimeType.JPEG:
      case MimeType.PNG:
        return ImageIcon;
      case MimeType.PDF:
        return PdfIcon;
      case MimeType.DOCX:
        return WordIcon;
      case MimeType.XLSX:
        return ExcelIcon;
      default:
        return DocumentIcon;
    }
  }, [attachment]);

  const handleAttachFile = useCallback<ChangeEventHandler<HTMLInputElement>>(
    async (event) => {
      if (!event.target.files) return;

      const file = event.target.files[0];

      if (!validateAttachment(file)) return;

      const base64 = await FileUtils.toBase64String(file);

      dispatch(
        updateCommonRequestData({
          attachment: {
            base64,
            name: file.name,
            type: file.type as MimeType,
          },
        })
      );
    },
    [dispatch, validateAttachment]
  );

  const handleRemoveAttachment = useCallback(() => {
    dispatch(updateCommonRequestData({ attachment: null }));
  }, [dispatch]);

  const theme = useTheme();
  const isMobile = useMobileLayout();

  // Check if all required fields have length > 0 (validation happens on submit)
  const isFormValid = useMemo(() => {
    const hasContactName = contactName && contactName.trim().length > 0;
    const hasContactEmail = contactEmail && contactEmail.trim().length > 0;
    const hasMessage = message && message.trim().length > 0;

    return hasContactName && hasContactEmail && hasMessage;
  }, [contactName, contactEmail, message]);

  // Get specific validation error messages
  const validationErrorMessage = useMemo(() => {
    if (!validationWarningShown) return null;

    // Default message for empty fields
    return "Please finish filling out information";
  }, [validationWarningShown]);

  return (
    <Box sx={{ width: "100%" }}>
      {attachment && (
        <Box pl={5} pb={isMobile ? 4 : undefined}>
          <Chip Icon={AttachmentIcon} label={attachment.name} onDelete={handleRemoveAttachment} />
          <Box fontSize={14} mt={2.5}>
            Up to one document may be attached; max file size of 5MB
          </Box>
        </Box>
      )}
      <Grid
        item
        container
        sx={{
          flexDirection: isMobile ? "column" : "row",
          justifyContent: isMobile ? "center" : "flex-start",
          width: isMobile ? "auto" : "100%",
        }}
        data-testid="request-contact-update-controls"
      >
        <Grid
          item
          container
          xs={isMobile ? undefined : 9}
          sx={{
            flexDirection: isMobile ? "column-reverse" : "row",
            alignItems: "center",
            position: "relative",
          }}
        >
          <Grid item>
            <SelfServiceButton
              upload
              fullWidth
              size={isMobile ? "small" : "medium"}
              onClick={noop}
              shape="rounded"
              startIcon={<Clip />}
              disabled={loading || !!attachment}
            >
              Attach a document
              <input
                type="file"
                id="attachment"
                name="attachment"
                hidden
                accept={accept}
                value=""
                onChange={handleAttachFile}
              />
            </SelfServiceButton>
          </Grid>
          {validationWarningShown && validationErrorMessage && (
            <Grid
              item
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                paddingBottom: isMobile ? 6 : "unset",
              }}
            >
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                color="warning.main"
              >
                <Box fontSize={15} fontWeight="500" display="flex" alignItems="center">
                  <RoundedIcon Icon={ExclamationIcon} variant="warning" />
                  <Box ml={2}>ERROR</Box>
                </Box>
                <Box fontSize={18} mt={1}>
                  {validationErrorMessage}
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
        <Grid
          item
          sx={{
            paddingTop: isMobile ? theme.spacing(4) : "unset",
            marginLeft: isMobile ? 0 : "auto",
            alignSelf: isMobile ? "center" : undefined,
          }}
        >
          <SelfServiceButton
            size={isMobile ? "small" : "medium"}
            onClick={onSubmit}
            shape="rounded"
            // LPC-10991: Include `loading` in the disabled check to block duplicate
            // submissions while the Salesforce case-create request is in flight.
            // Mirrors the InvestorRequest submit button (`disabled={loading || …}`).
            disabled={loading || !isFormValid || isImpersonating}
            sx={isMobile ? undefined : { minWidth: "unset" }}
          >
            Submit
          </SelfServiceButton>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RequestContactUpdateControls;
